import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Gift, Loader2, LogIn, Eye, EyeOff, MailCheck, Sparkles, UserPlus, Check, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { signInWithOAuth } from '@/integrations/coderblock/auth'
import { fireConfetti } from '@/lib/confetti'
import { useAuth } from '@/hooks/useAuth'
import { useLanguage } from '@/context/LanguageContext'

const t = {
  it: {
    accesso: 'Accedi',
    registrati: 'Registrati',
    bentornato: 'Bentornatə!',
    accediSub: 'Accedi per gestire la tua lista dei desideri.',
    googleCaricamento: 'Accesso con Google…',
    google: 'Continua con Google',
    oppure: 'oppure con email',
    email: 'Email',
    password: 'Password',
    mostra: 'Mostra password',
    nascondi: 'Nascondi password',
    accessoInCorso: 'Accesso in corso…',
    nonHaiAccount: 'Non hai un account?',
    popupBloccato: 'Il popup è stato bloccato dal browser: abilita i popup e riprova.',
    googleErrore: 'Accesso con Google non riuscito. Riprova.',
    creaWishlist: 'Crea la tua wishlist',
    creaSub: 'La tua lista dei desideri online, in un click.',
    nomePubblico: 'Nome pubblico',
    nomePh: 'Es. Sara Rossi',
    nomeSub: 'È il nome che vedranno chi visita la tua wishlist. Puoi cambiarlo quando vuoi.',
    linkPubblico: 'Link pubblico',
    linkSub: "È l'indirizzo web della tua wishlist da condividere con amici e parenti. Non si usa per il login: sceglilo con cura perché si può cambiare una sola volta.",
    linkInvalid: '3-20 caratteri: lettere, numeri e underscore.',
    linkTaken: 'Questo link è già in uso, provane un altro.',
    linkAvailable: 'Link disponibile!',
    creazioneAccount: 'Creazione account…',
    creaAccount: 'Crea account',
    haiAccount: 'Hai già un account?',
    confermaTitolo: 'Controlla la tua email per confermare la registrazione 📫',
    confermaSub: (email: string) => `Ti abbiamo inviato un link di conferma a ${email}. Aprilo per attivare il tuo account.`,
    vaiLogin: 'Vai al login',
    usernameInvalid: 'Lo username deve essere di 3-20 caratteri: lettere, numeri e underscore.',
    passwordCorta: 'La password deve contenere almeno 6 caratteri.',
    placeholderEmail: 'tu@esempio.it',
    placeholderUsername: 'sara_rossi',
    placeholderUsernameOnb: 'mario_rossi',
  },
  en: {
    accesso: 'Sign in',
    registrati: 'Sign up',
    bentornato: 'Welcome back!',
    accediSub: 'Sign in to manage your wishlist.',
    googleCaricamento: 'Signing in with Google…',
    google: 'Continue with Google',
    oppure: 'or with email',
    email: 'Email',
    password: 'Password',
    mostra: 'Show password',
    nascondi: 'Hide password',
    accessoInCorso: 'Signing in…',
    nonHaiAccount: "Don't have an account?",
    popupBloccato: 'The popup was blocked by your browser: enable popups and try again.',
    googleErrore: 'Google sign-in failed. Please try again.',
    creaWishlist: 'Create your wishlist',
    creaSub: 'Your online wishlist, in one click.',
    nomePubblico: 'Display name',
    nomePh: 'e.g. Sara Rossi',
    nomeSub: 'This is the name visitors will see on your wishlist. You can change it anytime.',
    linkPubblico: 'Public link',
    linkSub: "This is the web address of your wishlist to share with friends and family. It's not used for login: choose carefully because it can only be changed once.",
    linkInvalid: '3-20 characters: letters, numbers and underscores.',
    linkTaken: 'This link is already taken, try another one.',
    linkAvailable: 'Link available!',
    creazioneAccount: 'Creating account…',
    creaAccount: 'Create account',
    haiAccount: 'Already have an account?',
    confermaTitolo: 'Check your email to confirm your registration 📫',
    confermaSub: (email: string) => `We've sent a confirmation link to ${email}. Open it to activate your account.`,
    vaiLogin: 'Go to login',
    usernameInvalid: 'Username must be 3-20 characters: letters, numbers and underscores.',
    passwordCorta: 'Password must be at least 6 characters.',
    placeholderEmail: 'you@example.com',
    placeholderUsername: 'sara_rossi',
    placeholderUsernameOnb: 'mario_rossi',
  },
}

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24Z" />
      <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
    </svg>
  )
}

const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/

export default function AuthPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isRegister = location.pathname === '/register'
  const { user, loading: authLoading } = useAuth()
  const { language } = useLanguage()
  const tt = t[language]

  useEffect(() => {
    if (user && !authLoading) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, authLoading, navigate])

  const [mode, setMode] = useState<'login' | 'register'>(isRegister ? 'register' : 'login')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')

  const trimmedUsername = useMemo(() => username.trim(), [username])
  const usernameValid = USERNAME_RE.test(trimmedUsername)

  const checkUsernameAvailability = async (value: string) => {
    if (!USERNAME_RE.test(value)) {
      setUsernameStatus('idle')
      return false
    }
    setUsernameStatus('checking')
    const { data: existing, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .ilike('username', value)

    if (checkError) {
      setUsernameStatus('idle')
      return false
    }
    const taken = !!existing && existing.length > 0
    setUsernameStatus(taken ? 'taken' : 'available')
    return !taken
  }

  useEffect(() => {
    if (mode !== 'register') return
    if (!usernameValid) {
      setUsernameStatus('idle')
      return
    }
    const t = setTimeout(() => {
      checkUsernameAvailability(trimmedUsername)
    }, 500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedUsername, usernameValid, mode])

  const switchMode = (newMode: 'login' | 'register') => {
    if (newMode === mode) return
    setError(null)
    setMode(newMode)
    navigate(newMode === 'register' ? '/register' : '/login', { replace: true })
  }

  const handleGoogle = () => {
    setError(null)
    setGoogleLoading(true)
    signInWithOAuth('google', {
      onComplete: (result) => {
        if (result.ok) {
          fireConfetti()
          navigate('/dashboard')
        } else {
          setGoogleLoading(false)
          setError(result.error === 'popup_blocked'
            ? tt.popupBloccato
            : tt.googleErrore)
        }
      },
    })
  }

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (err) {
      setError(err.message)
      return
    }
    fireConfetti()
    navigate('/dashboard')
  }

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!usernameValid) {
      setError(tt.usernameInvalid)
      return
    }
    if (password.length < 6) {
      setError(tt.passwordCorta)
      return
    }

    setLoading(true)
    const available = await checkUsernameAvailability(trimmedUsername)
    if (!available) {
      setLoading(false)
      return
    }

    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username: trimmedUsername, display_name: displayName.trim() || trimmedUsername },
        emailRedirectTo: `${window.location.origin}/email-confermata`,
      },
    })
    setLoading(false)

    if (signUpError) {
      setError(signUpError.message)
      return
    }
    if (data.session) {
      navigate('/dashboard')
    } else {
      setDone(true)
    }
  }

  const inputClass =
    'w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-muted-foreground/60'

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: 'url(/images/colorful-bg.webp)' }}
      />
      <motion.div
        className="relative w-full max-w-md glass rounded-lg p-8 sm:p-10 overflow-hidden"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/" className="flex items-center justify-center gap-2.5 text-lg font-semibold text-neutral-900 font-logo">
          <span className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </span>
          Make me a Gift
        </Link>

        {/* Mode switcher */}
        <div className="mt-6 flex rounded-lg bg-neutral-100 p-1">
          <button
            type="button"
            onClick={() => switchMode('login')}
            className={`flex-1 rounded-md py-2 text-sm font-semibold transition-all whitespace-nowrap ${
              mode === 'login' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {tt.accesso}
          </button>
          <button
            type="button"
            onClick={() => switchMode('register')}
            className={`flex-1 rounded-md py-2 text-sm font-semibold transition-all whitespace-nowrap ${
              mode === 'register' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {tt.registrati}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {mode === 'login' ? (
            <motion.div
              key="login"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{tt.bentornato}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{tt.accediSub}</p>

              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="mt-6 w-full bg-[#d9e5fc] border border-neutral-300 font-medium py-3.5 rounded-full hover:bg-[#eaf2fe] transition-colors disabled:opacity-50 flex items-center justify-center gap-3 text-sm"
              >
                {googleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <GoogleIcon />}
                {googleLoading ? tt.googleCaricamento : tt.google}
              </button>

              <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="h-px flex-1 bg-border" />
                {tt.oppure}
                <span className="h-px flex-1 bg-border" />
              </div>

              {error && (
                <div className="mt-6 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-1.5">{tt.email}</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={tt.placeholderEmail}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold mb-1.5">{tt.password}</label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? tt.nascondi : tt.mostra}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
                  {loading ? tt.accessoInCorso : tt.accesso}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                {tt.nonHaiAccount}{' '}
                <button type="button" onClick={() => switchMode('register')} className="font-bold text-primary hover:underline">
                  {tt.registrati}
                </button>
              </p>
            </motion.div>
          ) : done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="mt-8 text-center"
            >
              <div className="mx-auto w-16 h-16 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center">
                <MailCheck className="w-8 h-8" />
              </div>
              <h1 className="mt-6 text-2xl font-extrabold tracking-tight">
                {tt.confermaTitolo}
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                {tt.confermaSub(email)}
              </p>
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="inline-block mt-6 font-bold text-primary hover:underline"
              >
                {tt.vaiLogin}
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="register"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{tt.creaWishlist}</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {tt.creaSub}
              </p>

              <button
                type="button"
                onClick={handleGoogle}
                disabled={googleLoading}
                className="mt-6 w-full bg-[#d9e5fc] border border-neutral-300 font-medium py-3.5 rounded-full hover:bg-[#eaf2fe] transition-colors disabled:opacity-50 flex items-center justify-center gap-3 text-sm"
              >
                {googleLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <GoogleIcon />}
                {googleLoading ? tt.googleCaricamento : tt.google}
              </button>

              <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground/60">
                <span className="h-px flex-1 bg-border" />
                {tt.oppure}
                <span className="h-px flex-1 bg-border" />
              </div>

              {error && (
                <div className="mt-6 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
                  {error}
                </div>
              )}

              <form onSubmit={handleRegister} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="display-name" className="block text-sm font-semibold mb-1.5">
                    {tt.nomePubblico}
                  </label>
                  <input
                    id="display-name"
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={tt.nomePh}
                    className={inputClass}
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {tt.nomeSub}
                  </p>
                </div>
                <div>
                  <label htmlFor="username" className="block text-sm font-semibold mb-1.5">
                    {tt.linkPubblico}
                  </label>
                  <div className="flex w-full items-center rounded-xl border border-border bg-white transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary">
                    <span className="shrink-0 whitespace-nowrap py-3 pl-4 text-xs font-semibold text-muted-foreground sm:text-sm">makemeagift.shop/w/</span>
                    <input
                      id="username"
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder={tt.placeholderUsername}
                      className="min-w-0 flex-1 bg-transparent py-3 pl-1 pr-2 text-sm outline-none placeholder:text-muted-foreground/60"
                    />
                    {usernameValid && (
                      <span className="shrink-0 pr-3">
                        {usernameStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                        {usernameStatus === 'available' && <Check className="w-4 h-4 text-emerald-600" />}
                        {usernameStatus === 'taken' && <X className="w-4 h-4 text-red-600" />}
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {tt.linkSub}
                  </p>
                  {username.length > 0 && !usernameValid && (
                    <p className="mt-1 text-xs text-red-600">{tt.linkInvalid}</p>
                  )}
                  {usernameValid && usernameStatus === 'taken' && (
                    <p className="mt-1 text-xs text-red-600">{tt.linkTaken}</p>
                  )}
                  {usernameValid && usernameStatus === 'available' && (
                    <p className="mt-1 text-xs text-emerald-600">{tt.linkAvailable}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-1.5">{tt.email}</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={tt.placeholderEmail}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold mb-1.5">{tt.password}</label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? tt.nascondi : tt.mostra}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                  {loading ? tt.creazioneAccount : tt.creaAccount}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                {tt.haiAccount}{' '}
                <button type="button" onClick={() => switchMode('login')} className="font-bold text-primary hover:underline">
                  {tt.accesso}
                </button>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  )
}
